/* eslint-disable @next/next/no-img-element */
import { useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import clsx from "clsx";
import Tippy from "@tippyjs/react";

import "tippy.js/dist/tippy.css";
import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { apiGetAgenda } from "../../lib/api/engage";
import {
  getGroupForTitleEngage,
  getItemNumber,
  getItemNumberOnly,
  getGroupForTitle,
  tarrantInlineText,
} from "../../helpers/text";
import { calculateDuration } from "../../helpers/timing";
import { textToHtmlTinyMCE } from "../../helpers/converter";
import {
  getSubheadingByGroupId,
  getNameByGroupId,
  getParentByGroupId,
} from "../../helpers/arrays";
import { PRINT_HOST, ENV_MODE } from "../../helpers/printing";

import { PrinterIcon, DownloadIcon } from "@heroicons/react/outline";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";

// import AgendaTopicInfo from "./AgendaTopicInfoPlayer";
import AgendaTopicPlayerToc from "./AgendaTopicPlayerToc";
import Loading from "../Shared/Loading";
import EngageHeader from "../Builders/Header/EngageHeader";
import Supplemental from "./Supplemental";

const AgendaForPlayer = ({ agenda, client, activeTopic }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [topicDetails, setTopicDetails] = useState();
  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });
  const platform = useRecoilValue(engageState);

  useEffect(() => {
    async function fetchAgenda() {
      setLoading(true);
      const result = await apiGetAgenda(agenda);
      if (result) {
        setData(result);
        setLoading(false);
      } else {
        setError(true);
      }
    }
    if (agenda) {
      fetchAgenda();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agenda]);

  // if a chapter is selected it will show the information
  useEffect(() => {
    if (activeTopic) {
      if (activeTopic.hasDetails) {
        setTopicDetails(activeTopic);
        setShowDetails(true);
      }
      // check if the topic has details

      // if it does then trigger it show the information

      // if it doesn't then don't do anything
    }
  }, [activeTopic]);

  return (
    <div className="pb-sm antialiased">
      {data && (
        <div
          className={clsx(
            "hidden sm:block max-w-full 2xl:max-w-7/8 mx-auto px-4 sm:px-6 md:px-8 pt-6 bg-white z-40",
          )}
        >
          {data?.coverOptions?.useHeader && (
            <EngageHeader
              header={data?.pdfAgenda.headerDetails}
              agendaFont={data?.department.agendaFont}
            />
          )}
          <div className="pb-2 border-b border-gray-400 flex flex-col items-end mt-2">
            {/* <div className="col-span-2 2xl:col-span-2 flex flex-col 2xl:items-center items-end justify-end"></div> */}
            <div className="mt-4 lg:mt-0 flex justify-end items-end space-x-2 col-span-4 2xl:col-span-1">
              <div className="flex items-center space-x-2 mr-2">
                {platform?.allowSupplemental &&
                  data?.attachments?.length > 0 && (
                    <Tippy
                      content="Supplemental Materials"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div
                        className="flex px-2 py-0.5 md:px-4 md:py-1 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        onClick={() =>
                          setSupplemental({
                            visible: true,
                            meeting: data,
                          })
                        }
                      >
                        <HiOutlineDocumentDuplicate className="h-6 w-6 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </div>
                    </Tippy>
                  )}

                {data?.publishStatus !== "draftreview" && (
                  <Tippy
                    content="Print Agenda"
                    placement="bottom"
                    animation="scale"
                    delay={[500, 0]}
                  >
                    <div>
                      <a
                        href={`${PRINT_HOST}/agenda/${data?.id}?mode=${ENV_MODE}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                        title="Print Cover Sheet"
                        role="button"
                        aria-label="Print Cover Sheet"
                      >
                        <PrinterIcon className="h-6 w-6 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                      </a>
                    </div>
                  </Tippy>
                )}

                {data?.agendaPdfUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-2 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200 focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-6 w-6 text-blueGray-200 cursor-pointer p-0 md:p-1" />
                        </a>
                      </div>
                    </Tippy>
                  )}
                {data?.agendaPacketUrl &&
                  data?.publishStatus !== "draftreview" && (
                    <Tippy
                      content="Download Agenda Packet"
                      placement="bottom"
                      animation="scale"
                      delay={[500, 0]}
                    >
                      <div>
                        <a
                          href={data?.agendaPacketUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm md:text-base flex px-2 py-0.5 sm:px-4 md:py-1 sm:py-1.5 rounded-md items-center justify-evenly bg-blue-600 text-blueGray-200 hover:bg-blue-400 transform duration-200focus:outline-none"
                          title="Download Agenda Packet"
                          role="button"
                          aria-label="Download Agenda Packet"
                        >
                          <DownloadIcon className="h-6 w-6 text-blueGray-200 cursor-pointer p-0 md:p-1 mr-1" />
                          Packet
                        </a>
                      </div>
                    </Tippy>
                  )}
              </div>
            </div>
          </div>
          {data?.coverOptions?.useDateLocation && (
            <div
              className={clsx(
                "col-span-2 2xl:col-span-1 pt-1.5 pb-2",
                data?.department.agendaFont === "arial" && "font-arial",
                data?.department.agendaFont === "sans-serif" && "font-sans",
                data?.department.agendaFont === "timesNewRoman" &&
                  "font-timesNewRoman",
                data?.department.agendaFont === "noto" && "font-noto",
              )}
            >
              <p className="text-xs 2xl:text-lg text-black font-semibold">
                {data?.scheduleDate}
              </p>
              <p className="text-xs 2xl:text-lg text-black">
                {data?.room?.name}
              </p>
              <p className="text-xs 2xl:text-lg text-black">
                {data?.room?.address?.street}, {data?.room?.address?.city},
                {data?.room?.address?.state} {data?.room?.address?.zipCode}
              </p>
              {platform?.showDuration && (
                <p className="text-xs 2xl:text-base text-black italic">
                  Est. Duration: {calculateDuration(data?.actionTopics)}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Agenda Details */}
      {data && (
        <>
          {showDetails ? (
            <AgendaTopicPlayerToc
              visible={setShowDetails}
              topic={topicDetails.id}
              client={client}
            />
          ) : (
            <div className="px-4 sm:px-6 md:px-8">
              {/* Start Agenda */}
              <div className="mt-4">
                {/* Is Revised */}
                {data?.isRevised && (
                  <div className="mt-0.5 w-full text-center">
                    <p className="text-red-600 font-semibold text-sm xl:text-base 2xl:text-lg">
                      ** Revised **
                    </p>
                  </div>
                )}
                <div className={`relative mb-4 ${styles.tinyContainer}`}>
                  {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingTitle)}
                  {textToHtmlTinyMCE(data.pdfAgenda.agendaHeadingBlurb)}
                </div>
                {/* List */}
                <div>
                  {data &&
                    data?.groupOrder.map((group, index) => {
                      // const { name } = group;
                      const subheading = getSubheadingByGroupId(
                        group.id,
                        data?.groupSubheadings,
                      );
                      const parentGroup = getParentByGroupId(
                        group.id,
                        data?.groupSubheadings,
                      );
                      const name = getNameByGroupId(
                        group.id,
                        data?.groupSubheadings,
                        group.name,
                      );
                      const groupActions = data?.actionTopics
                        .map((topic) => {
                          if (
                            topic.agendaGroup.id === group.id &&
                            !topic.bulkTopic &&
                            !topic.registrationTopic
                          ) {
                            return topic;
                          }
                          return null;
                        })
                        .filter((topic) => topic !== null);
                      groupActions.sort((a, b) => a.order - b.order);
                      return (
                        <>
                          {group?.id.toString() ===
                            "682aa91b3c8f84005c5b9f81" && (
                            <div className={`mb-4 ${styles.tinyContainer}`}>
                              {textToHtmlTinyMCE(tarrantInlineText)}
                            </div>
                          )}
                          <div
                            key={`group-${index}`}
                            className={clsx(
                              data?.department.agendaFont === "arial" &&
                                "font-arial",
                              data?.department.agendaFont === "sans-serif" &&
                                "font-sans",
                              data?.department.agendaFont === "timesNewRoman" &&
                                "font-timesNewRoman",
                              data?.department.agendaFont === "noto" &&
                                "font-noto",
                              group?.parentGroup && "ml-8",
                              "mb-4 xl:mb-8",
                            )}
                          >
                            {/* <div className="border-b border-gray-800"> */}
                            <div
                              className={clsx(
                                data?.coverOptions?.useSeperators &&
                                  "border-b border-black",
                              )}
                            >
                              <p
                                className={clsx(
                                  data?.department.agendaOptions
                                    .agendaGroupBold && "font-semibold",
                                  data?.department.agendaOptions
                                    ?.agendaGroupFontSize &&
                                    `text-${data.department.agendaOptions.agendaGroupFontSize}`,
                                )}
                              >
                                {getGroupForTitleEngage(
                                  data?.groupNumbering,
                                  index,
                                  data?.groupOrder,
                                  data?.childNumbering,
                                )}
                                {name}
                              </p>
                            </div>
                            <p
                              className={clsx(
                                data?.department.agendaOptions
                                  .groupSubheadingItalic && "italic",
                                data?.department.agendaOptions
                                  ?.groupSubheadingSize &&
                                  `text-${data.department.agendaOptions.groupSubheadingSize}`,
                                "text-black pt-1 ml-2 whitespace-pre-line text-justify",
                              )}
                            >
                              {subheading}
                            </p>
                            {groupActions?.map((topic, i) => (
                              <div key={topic.id}>
                                <div
                                  className={clsx(
                                    i === 0 ? "mt-4" : "",
                                    "ml-4 xl:ml-8 mb-2 text-black select-none",
                                  )}
                                >
                                  <div className="flex">
                                    {/* Number - stays in place */}
                                    <span
                                      className={clsx(
                                        "text-sm text-black flex-shrink-0",
                                        data?.department.agendaOptions
                                          .titleBold && "font-semibold",
                                        data?.department.agendaOptions
                                          ?.titleFontSize &&
                                          `md:text-${data.department.agendaOptions.titleFontSize}`,
                                      )}
                                    >
                                      {getGroupForTitle(
                                        data?.groupNumbering,
                                        index,
                                        data?.groupOrder,
                                        data?.childNumbering,
                                      )}
                                      {getItemNumberOnly(
                                        data.actionTopics,
                                        data.itemNumbering,
                                        topic,
                                        i,
                                        data?.groupNumbering,
                                        data?.childNumbering,
                                      )}
                                    </span>
                                    <p
                                      className={clsx(
                                        data?.department.agendaOptions
                                          .titleBold && "font-semibold",
                                        data?.department.agendaOptions
                                          .titleUnderline &&
                                          "underline underline-offset-2",
                                        data?.department.agendaOptions
                                          ?.titleFontSize &&
                                          `md:text-${data.department.agendaOptions.titleFontSize}`,
                                        data?.department.agendaOptions
                                          ?.titleIndent
                                          ? "ml-12"
                                          : "ml-4",
                                        data?.department.agendaOptions
                                          ?.fullJustify
                                          ? "text-justify"
                                          : "text-left",
                                        "text-sm text-black whitespace-pre-line",
                                      )}
                                    >
                                      {topic.title}
                                    </p>
                                  </div>
                                  {topic.votedByBoard && (
                                    <p
                                      className={clsx(
                                        data?.department.agendaOptions
                                          ?.detailsFontSize &&
                                          `text-${data.department.agendaOptions.detailsFontSize}`,
                                        data?.department.agendaOptions
                                          .titleIndent
                                          ? "ml-20"
                                          : "ml-8",
                                        data?.department.agendaOptions
                                          ?.fullJustify
                                          ? "text-justify"
                                          : "text-left",
                                        "whitespace-pre-line",
                                      )}
                                    >
                                      &#40;{topic.votedByBoard?.name}&#41;
                                    </p>
                                  )}
                                  {topic.useActionInfo &&
                                    topic.useActionInfo === true && (
                                      <p
                                        className={clsx(
                                          data?.department.agendaOptions
                                            ?.titleIndent
                                            ? "ml-20"
                                            : "ml-8",
                                          data?.department.agendaOptions
                                            ?.fullJustify
                                            ? "text-justify"
                                            : "text-left",
                                          "whitespace-pre-line font-semibold text-red-700",
                                        )}
                                      >
                                        {topic.actionInfo}
                                      </p>
                                    )}
                                  {topic.topicBlurb.length > 0 && (
                                    <span
                                      className={clsx(
                                        data?.department.agendaOptions
                                          ?.detailsFontSize &&
                                          `text-${data.department.agendaOptions.detailsFontSize}`,
                                        data?.department.agendaOptions
                                          .titleIndent
                                          ? "ml-20"
                                          : "ml-8",
                                        data?.department.agendaOptions
                                          ?.fullJustify
                                          ? "text-justify"
                                          : "text-left",
                                        "whitespace-pre-line inline-block",
                                      )}
                                    >
                                      {topic.topicBlurb}
                                    </span>
                                  )}

                                  {client !== "broomfield" &&
                                    topic.subTopics?.length > 0 &&
                                    topic.subTopics.map((sub, sIdx) => {
                                      if (client === "tarrantcountytx") {
                                        const letter = String.fromCharCode(
                                          97 + sIdx,
                                        ); // a, b, c, ...
                                        return (
                                          <p
                                            key={`sub-${sIdx}`}
                                            className={clsx(
                                              data?.department.agendaOptions
                                                ?.detailsFontSize &&
                                                `text-${data.department.agendaOptions.detailsFontSize}`,
                                              data?.department.agendaOptions
                                                .titleIndent
                                                ? "ml-20"
                                                : "ml-8",
                                              data?.department.agendaOptions
                                                ?.fullJustify
                                                ? "text-justify"
                                                : "text-left",
                                              "py-0.5",
                                            )}
                                          >
                                            {letter}&#41; {sub}
                                          </p>
                                        );
                                      } else {
                                        return (
                                          <p
                                            key={`sub-${sIdx}`}
                                            className={clsx(
                                              data?.department.agendaOptions
                                                ?.detailsFontSize &&
                                                `text-${data.department.agendaOptions.detailsFontSize}`,
                                              data?.department.agendaOptions
                                                .titleIndent
                                                ? "ml-20"
                                                : "ml-8",
                                              data?.department.agendaOptions
                                                ?.fullJustify
                                                ? "text-justify"
                                                : "text-left",
                                              "py-0.5",
                                            )}
                                          >
                                            {![
                                              "santafespringsca",
                                              "coloradocountytx",
                                            ].includes(client) && (
                                              <>&#8212; </>
                                            )}{" "}
                                            {sub}
                                          </p>
                                        );
                                      }
                                    })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })}
                </div>
                <div className={`mt-4 ${styles.tinyContainer}`}>
                  {textToHtmlTinyMCE(data.pdfAgenda.agendaFooterBlurb)}
                </div>
                {platform && (
                  <div className="mt-6 flex w-full justify-between">
                    <div>
                      <div className={`${styles.tinyContainer}`}>
                        {textToHtmlTinyMCE(data.pdfAgenda.agendaFooterNote)}
                      </div>
                      {platform.useSignImage ? (
                        <>
                          <img
                            src={platform.signatureImage}
                            alt="signature"
                            className="h-auto w-52"
                          />
                        </>
                      ) : (
                        <>
                          {data?.signature && (
                            <>
                              <img
                                src={data?.signature}
                                alt="signature"
                                className="h-auto w-52"
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
                {platform?.useSeal && (
                  <div className="flex justify-end">
                    <img
                      src={platform.signatureSeal}
                      alt="signature"
                      className="h-32 w-32 rounded-full"
                    />
                  </div>
                )}
                <p className="text-xs 2xl:text-lg text-gray-700 italic">
                  Date Posted: {data?.publishedDate}
                </p>
              </div>
              {/* End Content */}
            </div>
          )}
        </>
      )}

      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}

      {loading && <Loading />}
      {error && (
        <div className="fixed z-10 inset-0 overflow-hidden">
          <div className="relative flex flex-col min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <img
              src="/images/jmCircle.svg"
              className="h-12 2xl:h-32 w-auto absolute top-4 left-4 z-50"
              alt="justMeetings logo"
            />
            <div className="fixed inset-0 bg-coolGray-900 transition-opacity"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>
            {/* Start Modal */}
            <div className="inline-block bg-coolGray-900 rounded-lg text-left transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:px-6 sm:pt-2 sm:pb-8">
              <div className="grid grid-cols-1 gap-2 my-10">
                <div>
                  <div className="mt-3 text-center">
                    <h3
                      className="text-lg 2xl:text-4xl leading-6 font-bold text-blueGray-200"
                      id="modal-title"
                    >
                      Invalid Meeting
                    </h3>
                    <div className="mt-2 xl:mt-8">
                      <p className="text-lg text-blueGray-300 mb-2 text-center font-medium">
                        Your url is using{" "}
                        <span className="font-bold text-white">{agenda}</span>,
                        which is not a valid meeting within JustMeetings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* End Modal */}
          </div>
        </div>
      )}
      {data?.publishStatus === "draft" && (
        <div className="absolute top-0 left-0 bg-slate-700 h-screen w-full z-50 flex items-center justify-center">
          <p className="text-lg 2xl:text-2xl font-bold text-white">
            This agenda is currently unavailable
          </p>
        </div>
      )}
    </div>
  );
};

export default AgendaForPlayer;
